import { SSE } from 'sse.js'
import { Temporal } from 'temporal-polyfill'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { toast } from '~/utils'

export const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT

type fetchOptions = RequestInit & {
  headers: HeadersInit & {
    'x-INSTANCE': string
    'x-JWT'?: string
    Accept: string
    'Content-Type'?: string
  }
}

type ApiErrorResponseBody = Record<string, unknown> & {
  error?: string
  code?: number
  message?: string
  detail?: string
}

const resolveApiErrorMessage = (
  data: ApiErrorResponseBody
): string | undefined => {
  if (typeof data.detail === 'string') {
    return data.detail
  }
  if (typeof data.message === 'string') {
    return data.message
  }
  if (typeof data.error === 'string') {
    return data.error
  }
  return undefined
}

const wrapErrorResponse = (
  fetchResponse: Response,
  data?: ApiErrorResponseBody
): Response => {
  if (!data) {
    return fetchResponse
  }

  const resolvedMessage = resolveApiErrorMessage(data)

  return new Proxy(fetchResponse, {
    get(target, prop, receiver) {
      if (prop === 'message' && resolvedMessage !== undefined) {
        return resolvedMessage
      }
      if (typeof prop === 'string' && prop in data) {
        return data[prop]
      }
      const value = Reflect.get(target, prop, receiver)
      if (typeof value === 'function') {
        return value.bind(target)
      }
      return value
    }
  }) as Response
}

export class ResponseError<
  T extends Record<string, unknown> = Record<string, unknown>
> extends Error {
  public response: Response
  public httpStatus?: number
  public responseData?: T
  constructor(
    message: string,
    res: Response,
    httpStatus?: number,
    responseData?: T
  ) {
    super(message)
    this.responseData = responseData
    this.response = wrapErrorResponse(
      res,
      responseData as ApiErrorResponseBody | undefined
    )
    this.httpStatus = httpStatus
  }
}

export type ApiResponseError = ResponseError & {
  response?: {
    error?: string
    code?: number
    message?: string
    detail?: string
    context?: Record<string, unknown>
  }
}

export type ApiViolation = {
  propertyPath: string
  message: string
}

export const getApiViolations = (error: ApiResponseError): ApiViolation[] => {
  const violations = error.responseData?.violations
  if (!Array.isArray(violations)) return []
  return violations.filter(
    (v): v is ApiViolation =>
      typeof v === 'object' &&
      v !== null &&
      typeof (v as ApiViolation).message === 'string'
  )
}

export type WithHydraPagination<T> = {
  '@context': string
  '@id': string
  '@type': string
  'hydra:member': T[]
  'hydra:totalItems': number
  'hydra:view'?: {
    '@id': string
    '@type': string
    'hydra:first'?: string
    'hydra:last'?: string
    'hydra:next'?: string
    'hydra:previous'?: string
  }
}

export const hydraNextPageParam =
  (itemsPerPage = 30) =>
  (lastPage: { 'hydra:totalItems'?: number }, allPages: unknown[]) => {
    const currentPage = allPages.length
    if ((lastPage['hydra:totalItems'] ?? 0) > currentPage * itemsPerPage) {
      return currentPage + 1
    }
    return undefined
  }

const buildFetchOptions = (
  method: string,
  data?: object | FormData,
  useLDJson?: boolean,
  instance?: string,
  signal?: AbortSignal,
  acceptHeader?: string
) => {
  const options: fetchOptions = {
    method: method ?? 'GET',
    headers: {
      'x-INSTANCE': instance ?? String(localStorage.getItem('instance')),
      Accept:
        acceptHeader ?? (useLDJson ? 'application/ld+json' : 'application/json')
    }
  }
  if (signal) {
    options.signal = signal
  }
  if (data) {
    if (data instanceof FormData) {
      options.body = data
    } else {
      options.body = JSON.stringify(data)
      options.headers['Content-Type'] =
        method.toUpperCase() === 'PATCH'
          ? 'application/merge-patch+json'
          : 'application/json'
    }
  }
  return options
}

interface fetchApiParams {
  url: string
  method?: string
  data?: object | FormData
  instance: string
  useLDJson?: boolean
  signal?: AbortSignal
}

export const fetchApi = async ({
  url,
  method,
  data,
  instance,
  useLDJson,
  signal
}: fetchApiParams) => {
  const init = buildFetchOptions(
    method ?? 'GET',
    data,
    useLDJson,
    instance,
    signal,
    undefined
  )
  init.headers['x-INSTANCE'] = (
    instance ?? String(localStorage.getItem('instance'))
  )
    .trim()
    .toLowerCase()

  const res = await fetch(API_ENDPOINT + url, init)
  if (!res.ok) {
    throw new ResponseError('Bad fetch response', res, res.status)
  }
  if (res.status === 204) {
    return
  }
  return res.json()
}

const filenameFromContentDisposition = (
  header: string | null
): string | null => {
  if (header == null || header === '') {
    return null
  }
  const quoted = /filename\*=UTF-8''([^;\s]+)/i.exec(header)
  if (quoted?.[1]) {
    try {
      return decodeURIComponent(quoted[1].replace(/"/g, ''))
    } catch {
      return quoted[1]
    }
  }
  const simple = /filename="([^"]+)"/i.exec(header)
  if (simple?.[1]) {
    return simple[1]
  }
  const bare = /filename=([^;\s]+)/i.exec(header)
  if (bare?.[1]) {
    return bare[1].replace(/^"(.*)"$/, '$1')
  }
  return null
}

const toJsonErrorResponse = async (res: Response): Promise<Response> => {
  const text = await res.text()
  try {
    JSON.parse(text)
    return new Response(text, {
      status: res.status,
      statusText: res.statusText,
      headers: { 'Content-Type': 'application/json' }
    })
  } catch {
    return new Response(
      JSON.stringify({
        message:
          text.trim() !== '' ? text : res.statusText || 'Bad fetch response'
      }),
      {
        status: res.status,
        statusText: res.statusText,
        headers: { 'Content-Type': 'application/json' }
      }
    )
  }
}

interface fetchAuthApiParams {
  url: string
  method?: string
  data?: object | FormData
  forceInstance?: string
  forceToken?: string
  useLDJson?: boolean
  showToast?: boolean
  signal?: AbortSignal
  accept?: string
  responseType?: 'json' | 'blob' | 'blobWithDisposition'
}

export const fetchApiWithToken = async <TResponse>({
  url,
  method,
  data,
  forceInstance,
  forceToken,
  useLDJson,
  showToast = true,
  signal,
  accept,
  responseType = 'json'
}: fetchAuthApiParams): Promise<TResponse> => {
  let instance = localStorage.getItem('instance')
  if (!instance) {
    instance = window.JobConfig?.instance?.computedName ?? ''
  }
  if (forceInstance) {
    instance = forceInstance
  }
  let token = localStorage.getItem('token')
  if (!token) {
    token = window.JobConfig?.user.jwt ?? ''
  }
  if (forceToken) {
    token = forceToken
  }
  if (!token || !instance) {
    throw new Error('Token or instance is missing')
  }

  const init = buildFetchOptions(
    method ? method.toUpperCase() : 'GET',
    data,
    useLDJson,
    instance,
    signal,
    accept
  )
  init.headers['x-JWT'] = forceToken ?? String(token)
  if (forceInstance) {
    init.headers['x-INSTANCE'] = forceInstance
  }

  const res = await fetch(API_ENDPOINT + url, init)
  if (!res.ok) {
    if (res.status === 401 && window.JobConfig === undefined) {
      console.warn('Token expired')
      localStorage.removeItem('id')
      localStorage.removeItem('token')
      localStorage.removeItem('instance')
      localStorage.removeItem('email')
      window.location.reload() // Temporary solution cause we cant access router here
    }

    const errorResponse = await toJsonErrorResponse(res)
    let errorData: Record<string, unknown> | undefined
    try {
      errorData = await errorResponse.clone().json()
    } catch {
      errorData = undefined
    }

    if (res.status === 403 && !errorData?.details && showToast) {
      toast(
        'Accès refusé',
        "Vous n'avez pas les droits pour effectuer cette action",
        ToastType.Danger
      )
    }
    throw new ResponseError(
      'Bad fetch response',
      errorResponse,
      res.status,
      errorData
    )
  }
  if (res.status === 204) {
    return Promise.resolve(true as TResponse)
  }
  if (responseType === 'blob') {
    return (await res.blob()) as TResponse
  }
  if (responseType === 'blobWithDisposition') {
    const filename = filenameFromContentDisposition(
      res.headers.get('Content-Disposition')
    )
    return { blob: await res.blob(), filename } as TResponse
  }
  return res.json()
}

const createGenericBatchFetcher =
  <T, K>(getKey: (item: T) => K) =>
  ({
    fetcher,
    delay = 10
  }: {
    fetcher: (keys: K[]) => Promise<T[]>
    delay?: number
  }) => {
    const keys = new Set<K>()
    const resolvers = new Map<K, (value: T) => void>()
    const rejectors = new Map<K, (error: Error) => void>()
    let batchTimeout: ReturnType<typeof setTimeout> | null = null

    const executeBatch = () => {
      const keysToFetch = Array.from(keys)
      keys.clear()
      const currentResolvers = new Map(resolvers)
      const currentRejectors = new Map(rejectors)
      resolvers.clear()
      rejectors.clear()
      batchTimeout = null

      fetcher(keysToFetch)
        .then((results) => {
          const seenKeys = new Set<K>()
          results.forEach((result) => {
            const key = getKey(result)
            seenKeys.add(key)
            const resolver = currentResolvers.get(key)
            if (resolver) {
              resolver(result)
            }
          })
          // Resolve also keys that were not found
          currentResolvers.forEach((resolver, key) => {
            if (!seenKeys.has(key)) {
              resolver(null as unknown as T)
            }
          })
        })
        .catch((error) => {
          currentRejectors.forEach((rejector) => {
            rejector(error)
          })
        })
    }

    return {
      fetch: (key: K): Promise<T> => {
        keys.add(key)

        const promise = new Promise<T>((resolve, reject) => {
          resolvers.set(key, resolve)
          rejectors.set(key, reject)
        })

        if (batchTimeout) {
          clearTimeout(batchTimeout)
        }
        batchTimeout = setTimeout(executeBatch, delay)

        return promise
      }
    }
  }

const BATCH_CHUNK_SIZE = 500

export const createBatchFetcher = <T extends { id: number }>(config: {
  fetcher: (ids: number[]) => Promise<T[]>
  delay?: number
}) =>
  createGenericBatchFetcher<T, number>((item) => item.id)({
    ...config,
    fetcher: async (ids) => {
      const chunks: number[][] = []
      for (let i = 0; i < ids.length; i += BATCH_CHUNK_SIZE) {
        chunks.push(ids.slice(i, i + BATCH_CHUNK_SIZE))
      }
      const results = await Promise.all(
        chunks.map((chunk) => config.fetcher(chunk))
      )
      return results.flat()
    }
  })

export const createBatchFetcherByKey =
  <T, K = string>(getKey: (item: T) => K) =>
  (config: { fetcher: (keys: K[]) => Promise<T[]>; delay?: number }) =>
    createGenericBatchFetcher<T, K>(getKey)(config)

const batchFetcherCache = new Map<
  string,
  { fetch: (key: any) => Promise<any> }
>()

export const getBatchFetcher = <T extends { id: number }>(
  contextKey: string,
  fetcher: (ids: number[]) => Promise<T[]>,
  delay?: number
) => {
  if (batchFetcherCache.has(contextKey)) {
    return batchFetcherCache.get(contextKey) as ReturnType<
      typeof createBatchFetcher<T>
    >
  }

  const batcher = createBatchFetcher<T>({
    fetcher,
    delay
  })

  batchFetcherCache.set(contextKey, batcher)
  return batcher
}

export const buildSSEClient = (url: string, options: object): SSE => {
  if (
    localStorage.getItem('instance') === null ||
    localStorage.getItem('token') === null
  ) {
    throw new Error('Token or instance is missing')
  }

  return new SSE(`${String(API_ENDPOINT)}/${url}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-INSTANCE': String(localStorage.getItem('instance')),
      'x-JWT': String(localStorage.getItem('token')),
      Accept: 'application/json'
    },
    method: 'POST',
    payload: JSON.stringify(options)
  })
}

export const extractIdFromIri = (
  iri: string | null | undefined
): number | null => {
  if (!iri) return null

  const match = iri.match(/\/v1\/(\w+)\/(\d+)/)
  if (!match?.[2]) return null

  return Number.parseInt(match[2], 10)
}

/** Ids of the IRIs belonging to one resource (`/v1/applicants/12`), other IRIs ignored */
export const extractIdsFromIris = (
  iris: string[],
  resource: string
): number[] =>
  iris.flatMap((iri) => {
    const match = iri.match(new RegExp(`^/v1/${resource}/(\\d+)$`))
    return match?.[1] ? [Number.parseInt(match[1], 10)] : []
  })

export const getUserIdFromContext = () => {
  let userId = localStorage.getItem('id')
  if (!userId) {
    userId = window.JobConfig ? String(window.JobConfig?.user.id) : null
  }
  return userId
}

export const getCurrentUserIri = () => {
  return `/v1/users/${getUserIdFromContext()}`
}

export const parseApiDate = (
  value: string | null | undefined
): Temporal.ZonedDateTime | null => {
  if (!value) {
    return null
  }

  try {
    return Temporal.Instant.from(value).toZonedDateTimeISO(
      Temporal.Now.timeZoneId()
    )
  } catch {
    return null
  }
}
